import React, { Component } from 'react';
import { Text, View, StyleSheet } from 'react-native';

import { COLOR, FONT, FONT_SIZE, THEME, __WEB__ } from '../../constants/styles';

import ASSETS from '../../constants/assets';

import {
  OPComponent,
  Container,
  DateUtils,
  ClipboardTouchable,
  ParsedText,
  IconRow,
  AttachmentsList,
  I18n,
  IconButton,
  Navigation,
  CheckButton,
} from '../components';

import {
  EVENT_TYPE_IMMUNIZATION,
  EVENT_TYPE_PHA,
  EVENT_TYPE_PHA_FLY,
} from '../../constants/eventTypes';

const CHECKLIST_UI_ITEMS = {
  [EVENT_TYPE_IMMUNIZATION]: {
    screenComponent: 'ImmunizationChecklist',
    screenTitle: I18n.ac('calendar.immunizationChecklist'),
    imageSource: ASSETS.immunizationsChecklist,
  },
  [EVENT_TYPE_PHA]: {
    screenComponent: 'PHAChecklist',
    screenTitle: I18n.ac('calendar.phaChecklist'),
    imageSource: ASSETS.immunizationsChecklist,
  },
  [EVENT_TYPE_PHA_FLY]: {
    screenComponent: 'PHAChecklist',
    screenTitle: I18n.ac('calendar.phaFlyChecklist'),
    imageSource: ASSETS.immunizationsChecklist,
    passProps: { fly: true },
  },
};

export default class EventDetailsView extends OPComponent {
  constructor(props) {
    super(props);
  }

  formatDateString(date, allDay) {
    const { event, isMilitaryEmployer } = this.props;
    const timeZone = event.timeZone;

    const dateStr = DateUtils.formatDateString(date, timeZone);
    return `${dateStr}, ${
      allDay
        ? I18n.t('calendar.allDay')
        : isMilitaryEmployer
          ? DateUtils.formatTimeStringMilitary(date, timeZone)
          : DateUtils.formatTimeString(date, timeZone)
    }\n${timeZone}`;
  }

  openChecklist = () => {
    const event = this.props.event;
    const checklistUIItems = CHECKLIST_UI_ITEMS[event.eventTypeName];

    Navigation.openScreen(
      {
        screen: checklistUIItems.screenComponent,
        title: checklistUIItems.screenTitle,
        passProps: checklistUIItems.passProps,
      },
      this.props.componentId,
      true
    );
  };

  renderEventTypeFeature() {
    const event = this.props.event;
    const checklistUIItems = CHECKLIST_UI_ITEMS[event.eventTypeName];

    if (!checklistUIItems) return null;

    let canView;
    switch (event.eventTypeName) {
      case EVENT_TYPE_IMMUNIZATION:
        canView = this.props.canViewEditingCasePatientImmunizations;
        break;
      case EVENT_TYPE_PHA:
      case EVENT_TYPE_PHA_FLY:
        canView = this.props.canViewPHAChecklist;
        break;
      default:
        canView = false;
    }

    if (!canView) return null;

    return (
      <View style={styles.eventTypeFeatureContainer}>
        <IconButton
          label={checklistUIItems.screenTitle}
          value=""
          imageSource={checklistUIItems.imageSource}
          onPress={this.openChecklist}
        />
      </View>
    );
  }

  renderEventTypeDetails() {
    const event = this.props.event;

    if (event.eventTypeName === EVENT_TYPE_PHA) {
      const checked = !!event?.extraData?.withDRHA;
      return (
        <CheckButton
          checked={checked}
          label={'In Conjunction with a DRHA1 or DRHA2'}
          value={checked}
          labelStyle={styles.drhaTextStyle}
        />
      );
    }
  }

  render() {
    const { event, instanceStart, instanceEnd, fields, caseName, test } = this.props;
    const ALL_FIELDS = !fields;

    if (!event) {
      return <View style={styles.container} />;
    }

    let displayStart = instanceStart || event.startDate;
    let displayEnd = instanceEnd || event.endDate;

    const locationString = event.location?.locationName || event.locationString || null;

    return (
      <Container actionSheet={true} scrollView={true}>
        <View style={THEME.header}>
          <Text style={styles.headerText}>
            {caseName ? `${caseName} - ` : ''}
            {event.eventName}
          </Text>
        </View>

        {this.renderEventTypeFeature()}
        {this.renderEventTypeDetails()}

        <View>
          <IconRow
            imageSource={require('../../../resources/icon-time.png')}
            label={I18n.t('calendar.starts')}
            value={this.formatDateString(displayStart, event.allDay)}
            topBorder={true}
          />
          {!event.allDay && (
            <IconRow
              imageSource={require('../../../resources/icon-time.png')}
              label={I18n.t('calendar.ends')}
              value={this.formatDateString(displayEnd)}
            />
          )}
          <IconRow
            imageSource={require('../../../resources/icon-location.png')}
            label={I18n.t('calendar.location')}
            value={locationString}
            iconStyle={{
              width: 13,
              height: 17,
              marginTop: 1,
              marginLeft: 2,
              marginRight: 1,
            }}
          />

          <IconRow
            imageSource={require('../../../resources/icon-event-type.png')}
            label={I18n.t('calendar.eventType')}
            value={I18n.t(`calendar.eventTypes.${event.eventTypeName}`, {
              defaults: [{ message: event.eventTypeName }],
            })}
          />

          <IconRow
            imageSource={require('../../../resources/icon-phone.png')}
            label={I18n.t('phone')}
            value={event.phone}
          />

          <IconRow
            imageSource={require('../../../resources/icon-reminder.png')}
            label={I18n.t('calendar.reminder')}
            value={event.reminderUIString}
          />
          <IconRow
            imageSource={require('../../../resources/icon-repeat.png')}
            label={I18n.t('calendar.repeat')}
            value={event.repeatUIString}
          />
          <IconRow
            imageSource={require('../../../resources/icon-flag.png')}
            label={I18n.capitalizeEach('calendar.assignedTo')}
            value={event.assigneeNameUIString}
          />
        </View>

        <View style={styles.notesContainer}>
          <Text style={styles.notesLabelText}>{I18n.ac('calendar.notes')}</Text>
          <ClipboardTouchable text={event.notes}>
            <ParsedText style={styles.notesValueText}>{this.props.eventNotes}</ParsedText>
          </ClipboardTouchable>

          <AttachmentsList
            constainerStyle={styles.attachmentsContainer}
            attachments={event.files}
          />
        </View>
      </Container>
    );
  }
}

const styles = StyleSheet.create({
  headerText: {
    ...THEME.headerText,
    fontSize: __WEB__ ? 20 : 18,
  },
  notesContainer: {
    paddingVertical: 20,
    paddingHorizontal: 25,
  },
  notesLabelText: {
    ...FONT.light,
    fontSize: FONT_SIZE.small,
    color: COLOR.text,
    marginBottom: 7,
  },
  notesValueText: {
    ...FONT.regular,
    fontSize: FONT_SIZE.xlarge,
    color: COLOR.text,
  },
  attachmentsContainer: {
    marginTop: 10,
  },
  eventTypeFeatureContainer: {
    paddingVertical: 20,
    borderTopWidth: 1,
    borderColor: COLOR.accent,
  },
  drhaTextStyle: {
    fontSize: __WEB__ ? 18 : 16,
  },
});
